/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { StyledButton } from "@insite/mobius/Clickable";
import { StyledClickable, StyledTypography } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import { xBaseTheme } from "@monsma/XDL/types";
import { css } from "styled-components";

const stickyColumnStyles = css`
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                css`
                    &:first-child:not(.no-sticky) {
                        position: sticky;
                        left: 0;
                        z-index: 1;
                        box-shadow: 4px 0 4px 0 ${safeColor(theme.colors.text.main).rgb().alpha(0.2).toString()};
                        ${TypographyStyle} {
                            max-width: 120px;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            display: block;
                        }
                    }
                    white-space: nowrap;
                `,
                null,
                null,
                null,
            ],
            "max",
        )}
`;

export default {
    defaultProps: {
        css: css`
            word-break: initial;
            overflow-wrap: break-word;
            word-wrap: break-word;
            border-spacing: 0;
            border-collapse: separate;
        `,
        cssOverrides: {
            head: css`
                background: ${getColor("text.inverse")};
            `,
            header: css`
                border: 1px solid ${getColor("common.ui1")};
                padding: 10px 15px;
                background: ${getColor("common.ui1")};
                color: ${getColor("text.inverse")};
                height: 38px;
                box-sizing: border-box;

                &:first-child {
                    border-radius: 4px 0 0 0;
                }

                &:last-child {
                    border-radius: 0 4px 0 0;
                }

                ${StyledButton} {
                    display: inline-flex;
                }
                ${stickyColumnStyles}
            `,
            body: css`
                border-bottom: 1px solid ${getColor("common.border")};
                ${StyledClickable} {
                    white-space: nowrap;
                    ${StyledTypography} {
                        color: ${getColor("text.main")};
                        font-size: 15px;
                        line-height: 19px;
                    }
                    &:hover {
                        ${StyledTypography} {
                            color: ${getColor("common.ui2")};
                        }
                    }
                }
            `,
            row: css`
                background: ${getColor("text.inverse")};
            `,
            evenRow: css`
                background: ${getColor("common.ui7")};
            `,
            cell: css`
                height: 50px;
                border: 1px solid ${getColor("common.border")};
                padding: 15px;
                background: inherit;
                white-space: normal;
                font-size: 15px;
                line-height: 19px;

                ${stickyColumnStyles};
                &.no-wrap {
                    white-space: nowrap;
                }
            `,
        },
        headerTypographyProps: {
            transform: "capitalize",
            size: "13px",
            lineHeight: "17px",
            weight: 700,
            color: "text.inverse",
        },
        cellTypographyProps: {
            size: "15px",
            lineHeight: "19px",
        },
        sortClickableProps: {
            css: css`
                justify-content: flex-start;
            `,
        },
        sortIconProps: {
            color: "secondary.contrast",
            height: 12,
            width: 10,
            css: css`
                margin-left: 5px;
            `,
        },
    },
} as xBaseTheme["dataTable"];
